import { useEffect, useRef, useCallback } from 'react';

export interface UrlFilterState {
  searchQuery?: string;
  filterAssignee?: string;
  filterStatus?: string;
  filterPriority?: string;
  filterType?: string;
  showCompleted?: boolean;
  showArchived?: boolean;
}

export interface UseUrlFilterSyncParams {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  filterAssignee: string;
  setFilterAssignee: (a: string) => void;
  filterStatus: string;
  setFilterStatus: (s: string) => void;
  filterPriority: string;
  setFilterPriority: (p: string) => void;
  filterType: string;
  setFilterType: (t: string) => void;
  showCompleted: boolean;
  setShowCompleted: (c: boolean) => void;
  showArchived: boolean;
  setShowArchived: (a: boolean) => void;
  enabled?: boolean;
}

/**
 * Pure parser that extracts active filter query params from a search string or URL.
 */
export function parseUrlFilterParams(searchString: string): UrlFilterState {
  const result: UrlFilterState = {};
  if (!searchString) return result;

  const rawQuery = searchString.startsWith('?') ? searchString.slice(1) : searchString;
  const params = new URLSearchParams(rawQuery);

  const q = params.get('q') || params.get('search');
  if (q !== null && q.trim()) {
    result.searchQuery = q.trim();
  }

  const assignee = params.get('assignee') || params.get('filterAssignee');
  if (assignee !== null && assignee.trim() && assignee.trim() !== 'all') {
    result.filterAssignee = assignee.trim();
  }

  const status = params.get('status') || params.get('filterStatus');
  if (status !== null && status.trim() && status.trim() !== 'all') {
    result.filterStatus = status.trim();
  }

  const priority = params.get('priority') || params.get('filterPriority');
  if (priority !== null && priority.trim() && priority.trim() !== 'all') {
    result.filterPriority = priority.trim();
  }

  const type = params.get('type') || params.get('filterType');
  if (type !== null && type.trim() && type.trim() !== 'all') {
    result.filterType = type.trim();
  }

  const completed = params.get('completed') || params.get('showCompleted');
  if (completed !== null) {
    result.showCompleted = completed === 'true';
  }

  const archived = params.get('archived') || params.get('showArchived');
  if (archived !== null) {
    result.showArchived = archived === 'true';
  }

  return result;
}

/**
 * Pure builder that merges filter params into an existing search query string
 * while preserving all other existing parameters (e.g. view, projectId, workspace, invite).
 */
export function buildUrlWithFilterParams(
  currentSearch: string,
  filters: UrlFilterState
): string {
  const rawQuery = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
  const params = new URLSearchParams(rawQuery);

  // Search query (q)
  if (filters.searchQuery && filters.searchQuery.trim()) {
    params.set('q', filters.searchQuery.trim());
  } else {
    params.delete('q');
    params.delete('search');
  }

  // Filter assignee
  if (filters.filterAssignee && filters.filterAssignee !== 'all') {
    params.set('assignee', filters.filterAssignee);
  } else {
    params.delete('assignee');
    params.delete('filterAssignee');
  }

  // Filter status
  if (filters.filterStatus && filters.filterStatus !== 'all') {
    params.set('status', filters.filterStatus);
  } else {
    params.delete('status');
    params.delete('filterStatus');
  }

  // Filter priority
  if (filters.filterPriority && filters.filterPriority !== 'all') {
    params.set('priority', filters.filterPriority);
  } else {
    params.delete('priority');
    params.delete('filterPriority');
  }

  // Filter type
  if (filters.filterType && filters.filterType !== 'all') {
    params.set('type', filters.filterType);
  } else {
    params.delete('type');
    params.delete('filterType');
  }

  // Show completed (default is true; only include in URL when explicitly false)
  if (filters.showCompleted !== undefined && filters.showCompleted === false) {
    params.set('completed', 'false');
  } else {
    params.delete('completed');
    params.delete('showCompleted');
  }

  // Show archived (default is false; only include in URL when explicitly true)
  if (filters.showArchived !== undefined && filters.showArchived === true) {
    params.set('archived', 'true');
  } else {
    params.delete('archived');
    params.delete('showArchived');
  }

  const res = params.toString();
  return res ? `?${res}` : '';
}

/**
 * Hook synchronizing active task filter states with browser URL search parameters via window.history.replaceState.
 * Allows filtered views to be shared via URL or bookmarked without disrupting browser history.
 */
export function useUrlFilterSync({
  searchQuery,
  setSearchQuery,
  filterAssignee,
  setFilterAssignee,
  filterStatus,
  setFilterStatus,
  filterPriority,
  setFilterPriority,
  filterType,
  setFilterType,
  showCompleted,
  setShowCompleted,
  showArchived,
  setShowArchived,
  enabled = true,
}: UseUrlFilterSyncParams) {
  const lastSerializedQueryRef = useRef<string | null>(null);

  // 1. Initial restoration from URL on mount & popstate (restoring clean defaults when omitted)
  const syncFromUrl = useCallback(() => {
    if (typeof window === 'undefined' || !enabled) return;
    const urlFilters = parseUrlFilterParams(window.location.search);

    setSearchQuery(urlFilters.searchQuery ?? '');
    setFilterAssignee(urlFilters.filterAssignee ?? 'all');
    setFilterStatus(urlFilters.filterStatus ?? 'all');
    setFilterPriority(urlFilters.filterPriority ?? 'all');
    setFilterType(urlFilters.filterType ?? 'all');
    setShowCompleted(urlFilters.showCompleted ?? true);
    setShowArchived(urlFilters.showArchived ?? false);
  }, [
    enabled,
    setSearchQuery,
    setFilterAssignee,
    setFilterStatus,
    setFilterPriority,
    setFilterType,
    setShowCompleted,
    setShowArchived,
  ]);

  useEffect(() => {
    syncFromUrl();

    const handlePopState = () => {
      syncFromUrl();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [syncFromUrl]);

  // 2. Synchronize current filters to URL search string via replaceState
  useEffect(() => {
    if (typeof window === 'undefined' || !enabled) return;

    const currentSearch = window.location.search;
    const newSearch = buildUrlWithFilterParams(currentSearch, {
      searchQuery,
      filterAssignee,
      filterStatus,
      filterPriority,
      filterType,
      showCompleted,
      showArchived,
    });

    if (newSearch !== currentSearch && newSearch !== lastSerializedQueryRef.current) {
      lastSerializedQueryRef.current = newSearch;
      const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }, [
    enabled,
    searchQuery,
    filterAssignee,
    filterStatus,
    filterPriority,
    filterType,
    showCompleted,
    showArchived,
  ]);
}
